
/* ==========================================================================
   TIIAN V3 — Parcely Leaflet clustering
   - Standalone; no external clustering plugin.
   - Preserves Parcely's original marker and LayerGroup instances.
   - Caches projected coordinates by zoom level.
   - Does not recluster on pan.
   - Reuses cluster marker instances through a pool.
   - Uses Set-based marker tracking.
   - Batches updates using requestAnimationFrame.
   - Invalidates cached coordinates when markers move.
   - Cleans up listeners and cluster state on map unload.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__tiianParcelyClusterV3) return;
  window.__tiianParcelyClusterV3 = true;

  var GRID_PX = 80;
  var SHOW_ALL_ZOOM = 17;
  var CLUSTER_SIZE = 44;
  var LOG = false;

  function log() {
    if (LOG) {
      console.log.apply(console, ['[TIIAN Clusters V3]'].concat(
        Array.prototype.slice.call(arguments)
      ));
    }
  }

  function isParcelyMap(container) {
    if (!container) return false;

    return !!(
      container.matches?.('.parcely_map') ||
      container.closest?.('#parcelyWidgetModal') ||
      container.closest?.('#parcelyWidget') ||
      container.closest?.('[id*="parcelyWidget"]')
    );
  }

  function install(L) {
    if (!L || !L.Map || !L.Marker) return false;

    if (L.Map.prototype.__tiianClusterV3Installed) return true;

    L.Map.prototype.__tiianClusterV3Installed = true;

    L.Map.addInitHook(function () {
      var map = this;
      var attempts = 0;
      var timer = null;

      function attempt() {
        if (!map._container) return true;

        if (isParcelyMap(map.getContainer())) {
          initClustering(map, L);
          return true;
        }

        return false;
      }

      if (attempt()) return;

      timer = window.setInterval(function () {
        attempts++;

        if (attempt() || attempts >= 40 || !map._container) {
          window.clearInterval(timer);
          timer = null;
        }
      }, 250);

      map.once('unload', function () {
        if (timer !== null) {
          window.clearInterval(timer);
          timer = null;
        }
      });
    });

    log('Leaflet hook installed');
    return true;
  }

  function initClustering(map, L) {
    if (map.__tiianClustersV3) return;
    map.__tiianClustersV3 = true;

    // All original marker instances currently associated with this map.
    var markers = new Set();

    // Active cluster markers and reusable inactive cluster markers.
    var clusters = [];
    var clusterPool = [];

    var rafId = 0;
    var destroyed = false;

    // Preserve the original methods to avoid any future map.addLayer
    // or map.removeLayer wrappers being invoked for our own clusters.
    var originalAddLayer = map.addLayer;
    var originalRemoveLayer = map.removeLayer;

    function isCluster(layer) {
      return !!(layer && layer.__tiianClusterV3);
    }

    function setMarkerVisible(marker, visible) {
      var opacity = visible
        ? marker.__tiianClusterOriginalOpacity
        : 0;

      if (marker.options.opacity !== opacity) {
        marker.setOpacity(opacity);
      }
    }

    function invalidateProjection(marker) {
      marker.__tiianProjectedByZoom = null;
    }

    function getProjectedPoint(marker, zoom) {
      var cache = marker.__tiianProjectedByZoom;

      if (!cache) {
        cache = Object.create(null);
        marker.__tiianProjectedByZoom = cache;
      }

      var key = String(zoom);

      if (!cache[key]) {
        cache[key] = map.project(marker.getLatLng(), zoom);
      }

      return cache[key];
    }

    function onMarkerMove(event) {
      var marker = event.target;

      invalidateProjection(marker);
      schedule();
    }

    function remember(marker) {
      if (!(marker instanceof L.Marker) || isCluster(marker)) return;
      if (markers.has(marker)) return;

      markers.add(marker);

      marker.__tiianClusterOriginalOpacity =
        marker.options.opacity == null ? 1 : marker.options.opacity;

      marker.__tiianProjectedByZoom = null;
      marker.on('move', onMarkerMove);

      schedule();
    }

    function collect(layer) {
      if (!layer || isCluster(layer)) return;

      if (layer instanceof L.Marker) {
        remember(layer);
      } else if (typeof layer.eachLayer === 'function') {
        layer.eachLayer(collect);
      }
    }

    function forget(layer) {
      if (!layer || isCluster(layer)) return;

      if (layer instanceof L.Marker) {
        if (!markers.has(layer)) return;

        markers.delete(layer);
        layer.off('move', onMarkerMove);

        setMarkerVisible(layer, true);

        layer.__tiianProjectedByZoom = null;
        schedule();
      } else if (typeof layer.eachLayer === 'function') {
        layer.eachLayer(forget);
      }
    }

    function schedule() {
      if (destroyed || rafId) return;

      rafId = window.requestAnimationFrame(recluster);
    }

    // Move old cluster instances into a pool rather than destroying them.
    function releaseClusters() {
      for (var i = 0; i < clusters.length; i++) {
        var cluster = clusters[i];

        if (map.hasLayer(cluster)) {
          originalRemoveLayer.call(map, cluster);
        }

        clusterPool.push(cluster);
      }

      clusters.length = 0;
    }

    function createCluster() {
      var cluster = L.marker([0, 0], {
        icon: L.divIcon({
          html: '<span>2</span>',
          className: 'tiian-cluster tiian-cluster--small',
          iconSize: [CLUSTER_SIZE, CLUSTER_SIZE],
          iconAnchor: [CLUSTER_SIZE / 2, CLUSTER_SIZE / 2]
        }),
        keyboard: false,
        interactive: true,
        bubblingMouseEvents: false,
        zIndexOffset: 10000
      });

      cluster.__tiianClusterV3 = true;
      cluster.__tiianCount = -1;
      cluster.__tiianSize = '';

      // Resolve the current center when clicked. Do not capture stale
      // coordinates in the event handler because instances are reused.
      cluster.on('click', function (event) {
        if (event.originalEvent) {
          L.DomEvent.stopPropagation(event.originalEvent);
        }

        var nextZoom = Math.min(
          map.getZoom() + 2,
          map.getMaxZoom()
        );

        map.setView(cluster.getLatLng(), nextZoom, {
          animate: true
        });
      });

      return cluster;
    }

    function updateCluster(cluster, cell) {
      var count = cell.count;

      var center = L.latLng(
        cell.latSum / count,
        cell.lngSum / count
      );

      var size = count >= 100 ? 'large' :
        count >= 10 ? 'medium' : 'small';

      if (!cluster.getLatLng().equals(center)) {
        cluster.setLatLng(center);
      }

      if (
        cluster.__tiianCount !== count ||
        cluster.__tiianSize !== size
      ) {
        cluster.setIcon(L.divIcon({
          html: '<span>' + count + '</span>',
          className: 'tiian-cluster tiian-cluster--' + size,
          iconSize: [CLUSTER_SIZE, CLUSTER_SIZE],
          iconAnchor: [CLUSTER_SIZE / 2, CLUSTER_SIZE / 2]
        }));

        cluster.__tiianCount = count;
        cluster.__tiianSize = size;
      }

      // Restore a pooled cluster's visibility if it was previously hidden.
      if (cluster.options.opacity !== 1) {
        cluster.setOpacity(1);
      }

      if (!map.hasLayer(cluster)) {
        originalAddLayer.call(map, cluster);
      }

      clusters.push(cluster);
    }

    function restoreMarkers() {
      markers.forEach(function (marker) {
        if (map.hasLayer(marker)) {
          setMarkerVisible(marker, true);
        }
      });
    }

    function recluster() {
      rafId = 0;

      if (destroyed || !map._loaded || !map.getContainer()) return;

      var zoom = map.getZoom();

      // At high zoom, show the original markers without any clusters.
      if (zoom >= SHOW_ALL_ZOOM) {
        releaseClusters();
        restoreMarkers();

        log('Individual markers visible:', markers.size);
        return;
      }

      // Avoid needless cluster work at an unchanged zoom if no marker
      // data or marker positions have changed. Pan does not invalidate
      // this cache because the grid uses projected world coordinates.
      // A zoom change is tracked by lastZoom below.
      releaseClusters();
      restoreMarkers();

      var cells = Object.create(null);
      var keys = [];
      var activeCount = 0;

      markers.forEach(function (marker) {
        if (!map.hasLayer(marker)) return;

        var latLng = marker.getLatLng();
        if (!latLng) return;

        activeCount++;

        var point = getProjectedPoint(marker, zoom);

        var x = Math.floor(point.x / GRID_PX);
        var y = Math.floor(point.y / GRID_PX);
        var key = x + ':' + y;

        var cell = cells[key];

        if (!cell) {
          cell = cells[key] = {
            markers: [],
            count: 0,
            latSum: 0,
            lngSum: 0
          };

          keys.push(key);
        }

        cell.markers.push(marker);
        cell.count++;
        cell.latSum += latLng.lat;
        cell.lngSum += latLng.lng;
      });

      var poolIndex = 0;

      for (var i = 0; i < keys.length; i++) {
        var cell = cells[keys[i]];

        if (cell.count < 2) continue;

        for (var j = 0; j < cell.markers.length; j++) {
          setMarkerVisible(cell.markers[j], false);
        }

        var cluster = poolIndex < clusterPool.length
          ? clusterPool[poolIndex++]
          : createCluster();

        updateCluster(cluster, cell);
      }

      // Keep only the unused instances in the pool.
      clusterPool.length = poolIndex;

      log(
        'Reclustered:',
        activeCount,
        'markers into',
        clusters.length,
        'clusters'
      );
    }

    function onLayerAdd(event) {
      collect(event.layer);
    }

    function onLayerRemove(event) {
      forget(event.layer);
    }

    function onZoomEnd() {
      schedule();
    }

    function cleanup() {
      if (destroyed) return;
      destroyed = true;

      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }

      map.off('layeradd', onLayerAdd);
      map.off('layerremove', onLayerRemove);
      map.off('zoomend', onZoomEnd);

      markers.forEach(function (marker) {
        marker.off('move', onMarkerMove);
        setMarkerVisible(marker, true);
        marker.__tiianProjectedByZoom = null;
      });

      // The map may already be unloading. Remove our own state without
      // scheduling more work.
      clusters.concat(clusterPool).forEach(function (cluster) {
        if (map.hasLayer(cluster)) {
          originalRemoveLayer.call(map, cluster);
        }
      });

      markers.clear();
      clusters.length = 0;
      clusterPool.length = 0;

      log('Clustering cleaned up');
    }

    map.on('layeradd', onLayerAdd);
    map.on('layerremove', onLayerRemove);
    map.on('zoomend', onZoomEnd);
    map.once('unload', cleanup);

    // Capture existing markers and nested LayerGroups.
    map.eachLayer(collect);

    map.whenReady(schedule);

    log('Clustering initialized', map.getContainer());
  }

  // Install immediately or wait for asynchronously loaded Leaflet.
  if (!install(window.L)) {
    var pollCount = 0;

    var poll = window.setInterval(function () {
      pollCount++;

      if (install(window.L) || pollCount >= 120) {
        window.clearInterval(poll);

        if (!window.L) {
          log('Leaflet global was not found');
        }
      }
    }, 250);
  }

  // Styles are installed once.
  if (!document.getElementById('tiian-cluster-styles')) {
    var style = document.createElement('style');
    style.id = 'tiian-cluster-styles';

    style.textContent = `
      .tiian-cluster {
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${CLUSTER_SIZE}px;
        height: ${CLUSTER_SIZE}px;
        border: 3px solid #fff;
        border-radius: 50%;
        background: #111;
        color: #fff;
        font-size: 14px;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba(0,0,0,.3);
        cursor: pointer;
        contain: layout paint style;
        user-select: none;
      }

      .tiian-cluster--medium {
        background: #333;
      }

      .tiian-cluster--large {
        background: #c8ff00;
        color: #111;
      }
    `;

    document.head.appendChild(style);
  }
})();
